import { ApiError, type Api } from "../../data/api";
import {
  composeReason, parseSetup, validateGrant,
  type BillingEventRow, type EntitlementRow, type GrantInput, type GrantRow, type OrgOverviewRow, type PlanRow, type SetupStatus, type SubscriptionRow,
  type SupportLogRow,
} from "./model";
import { normalizeClientErrors, normalizeOps, type ClientErrors, type OpsMailHealth, type OpsSenderDomains, type OpsStatus } from "./ueberwachung-model";

/**
 * Lader und Schreibwege der Betreiber-Konsole. In Abfragen nur api.select und api.read (Lesefunktionen melden keine Änderung).
 * Agenturdaten liest der Betreiber nie direkt: platform_org_overview liefert je Agentur nur Zählwerte (0033).
 */

type Reader = Pick<Api, "select" | "read">;

const GRANT_COLUMNS = "id, org_id, platform_admin_id, reason, approved_by, starts_at, expires_at, revoked_at, created_at";

/** Supabase kann Zahlen aus bigint als Text liefern; die Anzeige rechnet mit Zahlen. */
function normalize(rows: OrgOverviewRow[]): OrgOverviewRow[] {
  return rows.map((r) => ({ ...r, storage_bytes: Number(r.storage_bytes ?? 0), chef_emails: r.chef_emails ?? [] }));
}

export interface Catalog {
  plans: PlanRow[];
  entitlements: EntitlementRow[];
}
async function loadCatalog(api: Reader): Promise<Catalog> {
  const [plans, entitlements] = await Promise.all([
    api.select<PlanRow>("plans", { columns: "key, name, price_month_cents, price_year_cents, active, sort_order", order: [["sort_order", "asc"], ["key", "asc"]] }),
    api.select<EntitlementRow>("plan_entitlements", { columns: "plan_key, key, value" }),
  ]);
  return { plans, entitlements };
}

/** 0390: nicht abgesagte Demo-Buchung eines Admins je Agentur (Testkonto-Abgleich über die E-Mail-Adresse) */
export interface DemoMarkRow { org_id: string; booking_id: string; starts_at: string; name: string }
export async function loadDemoMarks(api: Reader): Promise<DemoMarkRow[]> {
  const r = await api.read<DemoMarkRow[] | string>("platform_demo_marks", {});
  return (typeof r === "string" ? JSON.parse(r) : r) ?? [];
}

export interface AgenturenData extends Catalog {
  rows: OrgOverviewRow[];
}
export async function loadAgenturen(api: Reader): Promise<AgenturenData> {
  const [rows, catalog] = await Promise.all([api.read<OrgOverviewRow[]>("platform_org_overview", {}), loadCatalog(api)]);
  return { rows: normalize(rows ?? []), ...catalog };
}

export interface AgenturData extends Catalog {
  row: OrgOverviewRow | null;
  subscription: SubscriptionRow | null;
  events: BillingEventRow[];
  grants: GrantRow[];
  log: SupportLogRow[];
}
export async function loadAgentur(api: Reader, orgId: string): Promise<AgenturData> {
  const [rows, catalog, subs, events, grants, log] = await Promise.all([
    api.read<OrgOverviewRow[]>("platform_org_overview", { p_org: orgId }),
    loadCatalog(api),
    // Abo-Zeile: der Plattform-Admin darf sie lesen (subs_read); gebraucht wird nur, ob Stripe das Abo führt
    api.select<SubscriptionRow>("subscriptions", {
      columns: "org_id, plan_key, status, billing_interval, current_period_end, stripe_customer_id, stripe_subscription_id, updated_at",
      filters: [["org_id", "eq", orgId]],
    }),
    api.select<BillingEventRow>("billing_events", {
      columns: "stripe_event_id, type, org_id, received_at, outcome", filters: [["org_id", "eq", orgId]], order: [["received_at", "desc"]], limit: 25,
    }),
    api.select<GrantRow>("support_access_grants", { columns: GRANT_COLUMNS, filters: [["org_id", "eq", orgId]], order: [["created_at", "desc"]], limit: 50 }),
    api.read<SupportLogRow[]>("platform_support_log", { p_org: orgId }),
  ]);
  return { row: normalize(rows ?? [])[0] ?? null, subscription: subs[0] ?? null, ...catalog, events, grants, log: log ?? [] };
}

export interface SupportData {
  rows: OrgOverviewRow[];
  grants: GrantRow[];
  log: SupportLogRow[];
}
/** Eigene Zugänge (die Datenbank zeigt dem Plattform-Admin nur seine eigenen) und das eigene Support-Protokoll. */
export async function loadSupport(api: Reader): Promise<SupportData> {
  const [rows, grants, log] = await Promise.all([
    api.read<OrgOverviewRow[]>("platform_org_overview", {}),
    api.select<GrantRow>("support_access_grants", { columns: GRANT_COLUMNS, order: [["created_at", "desc"]], limit: 100 }),
    api.read<SupportLogRow[]>("platform_support_log", {}),
  ]);
  return { rows: normalize(rows ?? []), grants, log: log ?? [] };
}

/** Stand der Einrichtung einer Agentur: nur mit gültigem Support-Zugang lesbar (org_setup_status prüft das selbst). */
export async function loadSetup(api: Reader, orgId: string): Promise<SetupStatus | null> {
  return parseSetup(await api.read<unknown>("org_setup_status", { p_org: orgId }));
}

// ── Schreiben ───────────────────────────────────────────────────────────────────────────────────────

/**
 * Zugang öffnen: befristet, begründet, nur lesend (support_access_grants). Er gilt sofort; eine Freigabe durch den
 * Admin sieht die Datenbank nicht vor. Beginn setzt die Datenbank, das Ende rechnet sich ab jetzt.
 */
export async function openGrant(api: Pick<Api, "insert">, adminId: string, input: GrantInput, orgIds: readonly string[], nowMs = Date.now()): Promise<GrantRow> {
  const errors = validateGrant(input, orgIds);
  const first = Object.values(errors)[0];
  if (first) throw new ApiError(first);
  const rows = await api.insert<GrantRow>("support_access_grants", {
    org_id: input.orgId,
    platform_admin_id: adminId,
    reason: composeReason(input.ticket, input.reason),
    expires_at: new Date(nowMs + input.minutes * 60_000).toISOString(),
  });
  const row = rows[0];
  if (!row) throw new ApiError("Der Zugang wurde nicht angelegt. Bitte erneut versuchen.");
  return row;
}

/** Zugang vorzeitig beenden. false: war schon beendet oder gehört nicht zu diesem Konto. */
export async function endGrant(api: Pick<Api, "update">, grantId: string, nowMs = Date.now()): Promise<boolean> {
  const rows = await api.update<GrantRow>("support_access_grants", [["id", "eq", grantId], ["revoked_at", "is", null]], { revoked_at: new Date(nowMs).toISOString() });
  return rows.length > 0;
}

/**
 * Tarif umstellen (subscriptions.plan_key; nur Plattform-Admins dürfen das, Protokoll über audit_log). Nur für Abos ohne
 * Stripe: Führt Stripe das Abo, würde der nächste Webhook die Änderung überschreiben, also dort umstellen.
 */
export async function changePlan(api: Pick<Api, "update">, orgId: string, planKey: string, subscription: SubscriptionRow | null): Promise<SubscriptionRow> {
  if (!subscription) throw new ApiError("Diese Agentur hat kein Abo. Ein Abo entsteht beim Anlegen der Agentur oder über Stripe.");
  if (subscription.stripe_subscription_id) throw new ApiError("Dieses Abo führt Stripe. Bitte den Tarif in Stripe umstellen; der Webhook übernimmt ihn.");
  if (subscription.plan_key === planKey) throw new ApiError("Das ist bereits der aktuelle Tarif.");
  const rows = await api.update<SubscriptionRow>("subscriptions", [["org_id", "eq", orgId], ["plan_key", "eq", subscription.plan_key]], { plan_key: planKey });
  const row = rows[0];
  if (!row) throw new ApiError("Der Tarif wurde inzwischen geändert oder ist für dieses Konto nicht änderbar. Bitte die Seite neu laden.");
  return row;
}

// ── Abo, Testphase, Sperre, Admin, Anträge (0057) ─────────────────────────────────────────────

export interface SubscriptionRequestRow {
  id: string; org_id: string; org_name: string; plan_key: string; plan_name: string | null; billing_interval: "month" | "year"; note: string | null;
  requested_by_email: string | null; created_at: string; handled_at: string | null; outcome: string | null; outcome_note: string | null;
}
export interface AccountDeletionRow {
  user_id: string; email: string | null; requested_at: string; reason: string | null; processed_at: string | null; outcome: string | null;
  due_at: string; auto_at: string; orgs: string | null; blocked_reason: string | null;
}
export interface LifecycleLogRow {
  id: number; at: string; org_id: string | null; org_name: string | null; action: string; detail: Record<string, unknown>; error: string | null;
  /** 0103: storage_pending erledigt (Dateien über die Storage-Schnittstelle gelöscht), Versuche und letzter Fehler */
  resolved_at?: string | null; resolved_detail?: { deleted?: number; attempts?: number; last_error?: string | null; note?: string } | null;
}

export async function loadRequests(api: Reader, orgId: string | null = null): Promise<SubscriptionRequestRow[]> {
  return (await api.read<SubscriptionRequestRow[]>("platform_subscription_requests", orgId ? { p_org: orgId } : {})) ?? [];
}
export async function loadDeletions(api: Reader): Promise<AccountDeletionRow[]> {
  return (await api.read<AccountDeletionRow[]>("platform_account_deletions", {})) ?? [];
}
export async function loadLifecycleLog(api: Reader, limit = 50): Promise<LifecycleLogRow[]> {
  return api.select<LifecycleLogRow>("platform_lifecycle_log", { columns: "id, at, org_id, org_name, action, detail, error, resolved_at, resolved_detail", order: [["at", "desc"]], limit });
}
/** Speicher aufräumen (0103): Einträge storage_pending, offene zuerst. */
export async function loadStorageCleanup(api: Reader, limit = 50): Promise<LifecycleLogRow[]> {
  const rows = await api.select<LifecycleLogRow>("platform_lifecycle_log", {
    columns: "id, at, org_id, org_name, action, detail, error, resolved_at, resolved_detail", filters: [["action", "eq", "storage_pending"]], order: [["at", "desc"]], limit,
  });
  return [...rows].sort((a, b) => Number(!!a.resolved_at) - Number(!!b.resolved_at));
}

/** Abo und Agenturstatus gemeinsam freischalten (Rechnung/Überweisung); Laufzeitende leer: ein Monat bzw. ein Jahr ab heute. */
export async function activateSubscription(api: Pick<Api, "rpc">, orgId: string,
  input: { plan: string; interval: "month" | "year"; periodEnd: string | null; note: string | null; acceptOverage?: boolean }) {
  if (!input.plan) throw new ApiError("Bitte einen Tarif wählen.");
  const end = input.periodEnd ? new Date(`${input.periodEnd}T23:59:59Z`).toISOString() : null;
  // Überschreitet die Nutzung den Tarif, nur mit ausdrücklicher Bestätigung und Vermerk (0077, RECHTE-5)
  return api.rpc("platform_activate_subscription", { p_org: orgId, p_plan: input.plan, p_interval: input.interval, p_period_end: end, p_note: input.note?.trim() || null,
    p_accept_overage: !!input.acceptOverage });
}
/** Zweiten Faktor einer Person zurücksetzen (0077, RECHTE-7): Ticket und beschriebene Identitätsprüfung wie beim Chef-Wechsel. */
export async function resetSecondFactor(api: Pick<Api, "rpc">, input: { email: string; ticket: string; reason: string }) {
  return api.rpc<{ factors: number; orgs: number }>("platform_mfa_reset", { p_email: input.email.trim(), p_ticket: input.ticket.trim(), p_reason: input.reason.trim() });
}
/** K-379: über „konto-dienste“, damit ein Stripe-Probeabo das neue Ende übernimmt; ohne die Serverfunktion direkt in der Datenbank. */
export async function extendTrial(api: Pick<Api, "rpc" | "kontoDienste">, orgId: string, days: number, note: string | null) {
  if (!Number.isInteger(days) || days < 1 || days > 90) throw new ApiError("Verlängern um 1 bis 90 Tage.");
  if (api.kontoDienste) return api.kontoDienste({ action: "verlaengern", org: orgId, tage: days, notiz: note?.trim() || null });
  return api.rpc("platform_extend_trial", { p_org: orgId, p_days: days, p_note: note?.trim() || null });
}
/**
 * 0205: Kostenloser Testzugang ohne Zahlungsdaten, Dauer ab heute frei wählbar (auch als Verlängerung). Über die Serverfunktion
 * „konto-dienste“, damit ein hinterlegtes Stripe-Probeabo dasselbe Ende bekommt; ohne sie direkt in der Datenbank.
 */
export async function grantTrial(api: Pick<Api, "rpc" | "kontoDienste">, orgId: string, days: number, note: string | null): Promise<{ trial_ends_at: string; stripe?: string | null }> {
  if (!Number.isInteger(days) || days < 1 || days > 365) throw new ApiError("Dauer 1 bis 365 Tage.");
  if (api.kontoDienste) return api.kontoDienste({ action: "testzugang", org: orgId, tage: days, notiz: note?.trim() || null });
  return api.rpc("platform_grant_trial", { p_org: orgId, p_days: days, p_note: note?.trim() || null });
}
export async function setSuspended(api: Pick<Api, "rpc">, orgId: string, suspended: boolean, reason: string | null) {
  return api.rpc("platform_set_suspended", { p_org: orgId, p_suspended: suspended, p_reason: reason?.trim() || null });
}
export async function closeRequest(api: Pick<Api, "rpc">, id: string, outcome: "declined" | "contacted", note: string | null) {
  return api.rpc("platform_subscription_request_close", { p_id: id, p_outcome: outcome, p_note: note?.trim() || null });
}
/** Admin neu setzen (Ausfall ohne Übergabe): nur mit Ticket und beschriebener Identitätsprüfung, protokolliert. */
export async function setChef(api: Pick<Api, "rpc">, orgId: string, input: { email: string; ticket: string; reason: string; replaceEmail: string | null }) {
  return api.rpc<{ mode: "member" | "invited"; email: string }>("platform_set_chef", {
    p_org: orgId, p_email: input.email.trim(), p_ticket: input.ticket.trim(), p_reason: input.reason.trim(), p_replace_email: input.replaceEmail || null,
  });
}
export async function deleteAccount(api: Pick<Api, "rpc">, userId: string) {
  return api.rpc<"deleted" | "anonymized">("platform_account_delete", { p_user: userId });
}

// ── Betrieb: Zeitpläne, Serverfunktionen, Mailversand (0101) ───────────────────────────────────────

/** Stand für den Reiter Betrieb; die Datenbank prüft selbst, dass nur Plattform-Admins ihn lesen (platform_ops_status). */
export async function loadOpsStatus(api: Reader, hours = 24): Promise<OpsStatus> {
  return normalizeOps(await api.read<OpsStatus>("platform_ops_status", { p_hours: hours }));
}

/** Eigene Absenderdomains aller Agenturen und offene Löschungen beim Versanddienst (0102, platform_sender_domains). */
export async function loadSenderDomains(api: Reader): Promise<OpsSenderDomains> {
  const r = await api.read<OpsSenderDomains>("platform_sender_domains", {});
  return { ready: !!r?.ready, domains: r?.domains ?? [], removals: (r?.removals ?? []).map((x) => ({ ...x, attempts: Number(x.attempts ?? 0) })) };
}

/** Zustellbarkeit (0104): Stundenlimit, gebremste Agenturen, Rückläuferquote der letzten 7 Tage (platform_mail_health). */
export async function loadMailHealth(api: Reader): Promise<OpsMailHealth> {
  const r = await api.read<OpsMailHealth>("platform_mail_health", {});
  const n = (v: unknown) => (v === null || v === undefined ? null : Number(v));
  return {
    limit: Number(r?.limit ?? 0), limit_trial: Number(r?.limit_trial ?? 0), bounce_percent: Number(r?.bounce_percent ?? 5), bounce_min_mails: Number(r?.bounce_min_mails ?? 20),
    orgs: (r?.orgs ?? []).map((o) => ({ ...o, hourly_limit: Number(o.hourly_limit), sent_hour: Number(o.sent_hour), pending: Number(o.pending),
      throttled: Number(o.throttled), sent_7d: n(o.sent_7d), bounced_7d: n(o.bounced_7d), bounce_percent: n(o.bounce_percent) })),
  };
}

/** Sitzung beginnen: nur mit gültigem Zugang, landet im Protokoll der Agentur (open_support_session). */
export async function openSession(api: Pick<Api, "rpc">, orgId: string): Promise<boolean> {
  return (await api.rpc<boolean>("open_support_session", { p_org: orgId })) === true;
}

/** Fehler im Browser (0158), gebündelt je Fingerabdruck; nur Plattform-Admins (platform_client_errors). */
export async function loadClientErrors(api: Reader, days = 7): Promise<ClientErrors> {
  return normalizeClientErrors(await api.read<ClientErrors>("platform_client_errors", { p_days: days }));
}
